import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dumbbell, Apple, Pill, Camera, CheckCircle2, Circle,
  ChevronRight, BedDouble, ChevronDown, ChevronUp, Loader2, Sparkles,
  SkipForward
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { measurementsApi } from '../api/measurements';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import client from '../api/client';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { PullToRefreshIndicator } from '../components/PullToRefreshIndicator';
import { format } from 'date-fns';
import { pt as ptLocale, enUS as enLocale } from 'date-fns/locale';
import TargetMuscleBadges from '../components/TargetMuscleBadges';
import QuestionnaireTaskCard from '../components/QuestionnaireTaskCard';
import WelcomeTutorialModal from '../components/WelcomeTutorialModal';
import { getDisplayName } from '../lib/displayName';

function formatTime(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${hours}:${mm}${ampm}`;
}

function getGreetingKey() {
  const hour = new Date().getHours();
  if (hour < 12) return 'dashboard.goodMorning';
  if (hour < 18) return 'dashboard.goodAfternoon';
  return 'dashboard.goodEvening';
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimateIn(true));
    });
    return () => cancelAnimationFrame(id1);
  }, []);

  const { data: today, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'today'],
    queryFn: () => client.get('/dashboard/today').then(r => r.data),
    // Keep data fresh for a minute and show the previous data while refetching
    // so returning to the dashboard doesn't blank-spin every time. Task-
    // completing mutations still invalidate this key for immediate freshness.
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { isRefreshing, pullDistance, handlers: pullHandlers } = usePullToRefresh(
    () => refetch(),
    { disabled: isLoading }
  );

  const pageBgStyle = {
    background: isDark
      ? 'linear-gradient(165deg, #1a1a1a 0%, #0d0d0d 40%, #1a0a00 70%, #0d0d0d 100%)'
      : 'linear-gradient(165deg, #fafafa 0%, #f4f4f5 40%, #fff7ee 70%, #fafafa 100%)',
  };
  const pageBleedClasses = '-mx-4 -mt-4 md:-mx-6 md:-mt-6 lg:-mx-8 lg:-mt-8 -mb-24 md:-mb-8 px-4 pt-4 pb-24 md:px-6 md:pt-6 md:pb-8 lg:px-8 lg:pt-8 min-h-dvh';

  if (isLoading) {
    // Skeleton that mirrors the real layout (greeting → progress → task cards)
    // so the first load reads as "content arriving" instead of a blank spinner.
    return (
      <div className={`relative ${pageBleedClasses}`} style={pageBgStyle}>
        <div className="relative z-10 space-y-4 max-w-lg mx-auto" data-testid="dashboard-loading">
          <div className="space-y-2">
            <div className="h-3 w-24 rounded bg-zinc-300/50 dark:bg-white/10 animate-pulse" />
            <div className="h-7 w-48 rounded bg-zinc-300/50 dark:bg-white/10 animate-pulse" />
          </div>
          <div className="h-2 w-full rounded-full bg-zinc-300/40 dark:bg-white/5 animate-pulse" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 w-full rounded-2xl bg-zinc-300/40 dark:bg-white/5 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) return (
    <div className={`flex flex-col items-center justify-center gap-3 text-center ${pageBleedClasses}`} style={pageBgStyle}>
      <p className="text-zinc-600 dark:text-gray-400 text-sm">{t('dashboard.load_error')}</p>
    </div>
  );

  const workout = today?.workout || {};
  const diet = today?.diet || {};
  const supplements = today?.supplements || {};
  const checkin = today?.checkin || {};
  const questionnaires = today?.questionnaires || { has_pending: false, count: 0, items: [] };

  // Count completed tasks for progress bar
  const workoutDone = workout.type === 'rest' || workout.completed_today;
  const dietDone = diet.type === 'off' || diet.day_completed || (diet.goal_reached && diet.calories_target > 0);
  const suppsDone = supplements.has_plan && supplements.all_taken;
  const checkinNotDueYet = checkin.enabled && !checkin.completed_today && checkin.hours_until_due != null && checkin.hours_until_due > 24;
  const checkinDone = checkin.enabled && (checkin.completed_today || checkinNotDueYet);

  const activeTasks = [
    true, // workout always counts
    diet.type !== 'off' && diet.calories_target > 0, // diet only counts if tracking is on and there's a target
    supplements.has_plan, // supplements only count if there's a plan
    checkin.enabled, // checkin only counts if enabled
    questionnaires.has_pending, // questionnaires only count when there's something pending
  ];
  const completedTasks = [workoutDone, dietDone, suppsDone, checkinDone, false];
  const totalTasks = activeTasks.filter(Boolean).length;
  const completedCount = activeTasks.reduce((acc, active, i) => acc + (active && completedTasks[i] ? 1 : 0), 0);
  const allDone = completedCount === totalTasks && totalTasks > 0;

  const isUrgentCheckin = checkin.enabled && !checkin.completed_today && checkin.hours_until_due != null && checkin.hours_until_due <= 24;

  return (
    <div
      className={`relative ${pageBleedClasses}`}
      style={pageBgStyle}
      {...pullHandlers}
    >
      <WelcomeTutorialModal />
      {/* Ambient orange glow behind header — larger + stronger */}
      <div
        className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(255,92,0,0.45) 0%, rgba(255,92,0,0.12) 35%, transparent 70%)'
            : 'radial-gradient(circle, rgba(255,92,0,0.18) 0%, rgba(255,140,66,0.06) 35%, transparent 70%)',
          animation: 'dashPulseGlow 5s ease-in-out infinite',
          filter: 'blur(20px)',
        }}
        aria-hidden="true"
      />
      {/* Secondary lower glow for depth */}
      <div
        className="absolute top-1/3 right-[-100px] w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(255,140,66,0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(255,140,66,0.08) 0%, transparent 70%)',
          animation: 'dashPulseGlow 7s ease-in-out infinite 1.5s',
          filter: 'blur(30px)',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 space-y-4 max-w-lg mx-auto" data-testid="dashboard-page">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      {/* Header */}
      <div
        className={`transition-all duration-700 ease-out ${
          animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: '100ms' }}
      >
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-600 dark:text-gray-300 mb-1"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          {format(new Date(), 'EEEE, MMMM d')}
        </p>
        <h1
          className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white leading-tight"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          {t(getGreetingKey())},{' '}
          <span className="text-primary">{getDisplayName(user?.name).toUpperCase()}</span>
        </h1>
      </div>

      {/* Daily Progress */}
      <div
        className={`rounded-2xl px-4 py-3.5 backdrop-blur-xl transition-all duration-700 ease-out ${
          animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{
          background: isDark
            ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: `1px solid ${allDone ? 'rgba(34,197,94,0.35)' : 'rgba(255,92,0,0.28)'}`,
          boxShadow: isDark
            ? (allDone
              ? '0 4px 24px rgba(34,197,94,0.12), inset 0 1px 0 rgba(255,255,255,0.08)'
              : '0 4px 24px rgba(255,92,0,0.12), inset 0 1px 0 rgba(255,255,255,0.08)')
            : (allDone
              ? '0 4px 24px rgba(34,197,94,0.10), 0 1px 2px rgba(0,0,0,0.04)'
              : '0 4px 24px rgba(255,92,0,0.10), 0 1px 2px rgba(0,0,0,0.04)'),
          transitionDelay: '200ms',
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 dark:text-gray-400"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {t('dashboardExtras.todaysProgress')}
          </span>
          <span
            className="text-lg font-black"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              color: allDone ? '#4ade80' : (isDark ? '#ffffff' : '#18181b'),
              textShadow: allDone
                ? '0 0 12px rgba(74,222,128,0.5)'
                : (isDark ? '0 0 10px rgba(255,92,0,0.4)' : 'none'),
            }}
          >
            {completedCount} / {totalTasks}
          </span>
        </div>
        <div
          className="h-[3px] rounded-full overflow-hidden"
          style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
        >
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0}%`,
              background: allDone
                ? '#22c55e'
                : 'linear-gradient(to right, #FF5C00, #FF8C42)',
              boxShadow: allDone
                ? '0 0 12px rgba(34,197,94,0.6)'
                : '0 0 12px rgba(255,92,0,0.6)',
            }}
          />
        </div>
      </div>

      {/* Urgent check-in always floats to top */}
      {isUrgentCheckin && (
        <StaggerSlot index={0} animateIn={animateIn}>
          <CheckinCard checkin={checkin} urgent />
        </StaggerSlot>
      )}

      {/* Pending coach questionnaires float near the top — coach-assigned action */}
      {questionnaires.has_pending && (
        <StaggerSlot index={isUrgentCheckin ? 1 : 0} animateIn={animateIn}>
          <QuestionnaireTaskCard data={questionnaires} />
        </StaggerSlot>
      )}

      {/* Task Cards — undone first (natural order), done last (natural order), single urgent highlight on first pending */}
      {(() => {
        const cards = [
          { key: 'workout', done: workoutDone, el: <WorkoutCard workout={workout} /> },
          { key: 'diet',    done: dietDone,    el: <DietCard diet={diet} /> },
          ...(supplements.has_plan ? [{ key: 'supps', done: suppsDone, el: <SupplementsCard supplements={supplements} /> }] : []),
          ...(!isUrgentCheckin ? [{ key: 'checkin', done: checkinDone, el: <CheckinCard checkin={checkin} /> }] : []),
        ].sort((a, b) => (a.done === b.done ? 0 : a.done ? 1 : -1));

        const firstPendingIdx = isUrgentCheckin ? -1 : cards.findIndex(c => !c.done);

        const baseOffset = (isUrgentCheckin ? 1 : 0) + (questionnaires.has_pending ? 1 : 0);
        return cards.map((c, i) => {
          const urgent = i === firstPendingIdx;
          const child = React.cloneElement(c.el, { urgent });
          return (
            <StaggerSlot key={c.key} index={i + baseOffset} animateIn={animateIn}>
              {child}
            </StaggerSlot>
          );
        });
      })()}
      </div>
      <style>{`
        @keyframes dashPulseGlow {
          0%, 100% { opacity: 0.65; }
          50%      { opacity: 1; }
        }
        @keyframes dashSparkle {
          0%, 100% { opacity: 0.4; transform: scale(0.85) rotate(0deg); }
          50%      { opacity: 1;   transform: scale(1.1) rotate(15deg); }
        }
      `}</style>
    </div>
  );
}


// ─── Workout Card ────────────────────────────────────────────────────────────

function WorkoutCard({ workout, urgent }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const isDark = theme === 'dark';
  const done = workout.completed_today || workout.type === 'rest';
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

  // Only cycle/rotating schedules carry a pointer that can be advanced; a
  // weekly schedule is bound to the weekday and has nothing to skip.
  const canSkip = !done
    && workout.type === 'scheduled'
    && (workout.schedule_mode === 'cycle' || workout.schedule_mode === 'rotating');

  const skipWorkoutMutation = useMutation({
    mutationFn: async () => {
      const url = workout.routine_type === 'personal'
        ? `/routines/personal/${workout.routine_id}/skip-workout`
        : `/routines/${workout.routine_id}/skip-workout`;
      const res = await client.post(url);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.next_day_name
        ? t('dashboardExtras.skipWorkoutDone', { day: data.next_day_name })
        : t('dashboardExtras.skipWorkoutDoneGeneric'));
      setSkipConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['assignedRoutine'] });
      queryClient.invalidateQueries({ queryKey: ['personalRoutines'] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('dashboardExtras.skipWorkoutFailed'));
      setSkipConfirmOpen(false);
    },
  });

  const subtleBtnStyle = isDark
    ? { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.12)' }
    : { background: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.10)' };
  const subtleBtnClass = isDark
    ? 'w-full border text-white hover:bg-white/5'
    : 'w-full border text-zinc-900 hover:bg-black/5';

  if (workout.type === 'none' || !workout.type) {
    return (
      <TaskCard done={false} urgent={urgent} icon={<Dumbbell className="h-5 w-5" />} title={t('dashboardExtras.todaysWorkoutTitle')} color="orange">
        <p className="text-sm text-zinc-600 dark:text-gray-400 mb-3">{t('dashboardExtras.noWorkoutScheduled')}</p>
        <Button
          asChild
          size="sm"
          className={subtleBtnClass}
          style={subtleBtnStyle}
        >
          <Link to="/workout">{t('dashboardExtras.setUpWeekPlanner')}</Link>
        </Button>
      </TaskCard>
    );
  }

  if (workout.type === 'rest') {
    return (
      <TaskCard done urgent={urgent} icon={<BedDouble className="h-5 w-5" />} title={t('dashboardExtras.todaysWorkoutTitle')} color="orange">
        <p className="text-2xl font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {t('dashboardExtras.restDay')}
        </p>
        <p className="text-sm text-zinc-600 dark:text-gray-400">{t('dashboardExtras.recoveryIsTraining')}</p>
      </TaskCard>
    );
  }

  // A weekday may hold up to 2 workouts. Fall back to the top-level fields for
  // older API responses that don't yet send a `workouts` array.
  const workouts = workout.workouts?.length ? workout.workouts : [{
    day_id: workout.day_id,
    day_name: workout.day_name,
    exercise_count: workout.exercise_count,
    set_count: workout.set_count,
    work_set_count: workout.work_set_count,
    target_muscles: workout.target_muscles,
    completed: workout.completed_today,
    session_summary: workout.session_summary,
  }];

  return (
    <TaskCard done={done} urgent={urgent} icon={<Dumbbell className="h-5 w-5" />} title={t('dashboardExtras.todaysWorkoutTitle')} color="orange">
      <div className="space-y-3">
        {workouts.map((w, idx) => {
          const muscleStats = {};
          (w.target_muscles || []).forEach(m => { muscleStats[m] = 1; });
          return (
            <div key={w.day_id || idx} className={idx > 0 ? 'pt-3 border-t border-black/10 dark:border-white/10' : ''}>
              {workouts.length > 1 && (
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-gray-500 mb-0.5">
                  {t('dashboardExtras.workoutN', { num: idx + 1 })}
                </p>
              )}
              <p
                className="text-xl font-bold text-zinc-900 dark:text-white mb-1 leading-tight"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {w.day_name?.toUpperCase()}
              </p>
              <p className="text-xs text-zinc-600 dark:text-gray-400 mb-2">
                {t('dashboardExtras.exercisesSetsSummary', { exercises: w.exercise_count, sets: w.set_count, workSets: w.work_set_count })}
              </p>

              {Object.keys(muscleStats).length > 0 && (
                <div className="mb-3">
                  <TargetMuscleBadges stats={muscleStats} size="xs" />
                </div>
              )}

              {w.completed ? (
                <p className="text-sm text-green-800 dark:text-green-500 font-medium">
                  ✓ {t('dashboardExtras.completed')}{w.session_summary?.duration_minutes ? ` · ${w.session_summary.duration_minutes}m` : ''}
                  {w.session_summary?.prs_achieved > 0 ? ` · ${w.session_summary.prs_achieved} PRs` : ''}
                </p>
              ) : (
                <Button
                  asChild
                  className="w-full min-h-[52px] text-base font-bold text-white border-0 hover:opacity-95"
                  style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
                    boxShadow: '0 4px 20px rgba(255,92,0,0.35)',
                  }}
                >
                  <Link to={`/workout?routineId=${workout.routine_id}&dayId=${w.day_id}`}>
                    {t('dashboardExtras.startWorkoutBtn')}
                  </Link>
                </Button>
              )}
            </div>
          );
        })}

        {/* Trained it without logging? Advance the schedule so the cycle
            doesn't stay permanently one workout behind. */}
        {canSkip && (
          <button
            onClick={() => setSkipConfirmOpen(true)}
            disabled={skipWorkoutMutation.isPending}
            className="w-full min-h-[44px] flex items-center justify-center gap-2 text-xs font-medium text-zinc-600 dark:text-gray-400 hover:text-zinc-900 dark:hover:text-white transition-colors disabled:opacity-50"
            data-testid="dashboard-skip-workout-btn"
          >
            <SkipForward className="h-3.5 w-3.5" /> {t('dashboardExtras.skipWorkout')}
          </button>
        )}

        {done && (
          <Button asChild variant="ghost" size="sm" className="px-0 text-xs text-zinc-600 dark:text-gray-400">
            <Link to="/my-stats">{t('dashboardExtras.viewSession')} <ChevronRight className="h-3 w-3 ml-1" /></Link>
          </Button>
        )}
      </div>

      <Dialog open={skipConfirmOpen} onOpenChange={setSkipConfirmOpen}>
        {/* `compact` keeps this a centred card on mobile — without it DialogContent
            expands to a full-screen overlay, which is wrong for a 2-line confirm. */}
        <DialogContent className="max-w-sm" compact data-testid="dashboard-skip-workout-dialog">
          <DialogTitle className="text-lg font-bold">{t('dashboardExtras.skipWorkoutTitle')}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t('dashboardExtras.skipWorkoutBody')}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setSkipConfirmOpen(false)} className="flex-1">{t('common.cancel')}</Button>
            <Button
              onClick={() => skipWorkoutMutation.mutate()}
              disabled={skipWorkoutMutation.isPending}
              className="flex-1"
              data-testid="dashboard-confirm-skip-workout-btn"
            >
              {t('dashboardExtras.skipWorkoutConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </TaskCard>
  );
}


// ─── Diet Card ───────────────────────────────────────────────────────────────

function DietCard({ diet, urgent }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  if (diet.type === 'off') return null;
  const hasTarget = diet.calories_target > 0;
  const pct = hasTarget ? Math.min((diet.calories_logged / diet.calories_target) * 100, 100) : 0;
  const done = hasTarget && diet.goal_reached;

  return (
    <TaskCard done={done} urgent={urgent} icon={<Apple className="h-5 w-5" />} title={t('dashboardExtras.todaysDietTitle')} color="green">
      {hasTarget ? (
        <>
          <div className="flex items-center justify-between mb-1">
            <span className="text-lg font-bold text-zinc-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {diet.calories_logged}
              <span className="text-sm font-normal text-zinc-600 dark:text-gray-400"> / {diet.calories_target} kcal</span>
            </span>
            {done && <span className="text-xs text-green-800 dark:text-green-400 font-medium">{t('dashboardExtras.goalReached')} ✓</span>}
          </div>
          <Progress
            value={pct}
            className={`h-2 mb-3 ${done ? '[&>div]:bg-green-500' : '[&>div]:bg-primary'}`}
          />
          <div className="flex justify-around mb-3">
            <MacroPill label={t('dashboardExtras.proteinLabel')} logged={diet.protein_logged} target={diet.protein_target} />
            <MacroPill label={t('dashboardExtras.carbsLabel')} logged={diet.carbs_logged} target={diet.carbs_target} />
            <MacroPill label={t('dashboardExtras.fatLabel')} logged={diet.fat_logged} target={diet.fat_target} />
          </div>
        </>
      ) : (
        <p className="text-sm text-zinc-600 dark:text-gray-400 mb-3">
          {diet.calories_logged > 0
            ? t('dashboardExtras.kcalLoggedToday', { kcal: diet.calories_logged })
            : t('dashboardExtras.noFoodLoggedYet')}
        </p>
      )}
      {done ? (
        <Button
          asChild
          className={`w-full min-h-[48px] font-bold border ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-zinc-700 hover:bg-black/5'}`}
          style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
          }}
        >
          <Link to="/diet">{t('dashboardExtras.viewTodaysDiet')}</Link>
        </Button>
      ) : (
        <Button
          asChild
          className="w-full min-h-[48px] font-bold text-white border-0 hover:opacity-95"
          style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
            boxShadow: '0 4px 20px rgba(255,92,0,0.35)',
          }}
        >
          <Link to="/diet">{t('dashboardExtras.logMealBtn')}</Link>
        </Button>
      )}
    </TaskCard>
  );
}

function MacroPill({ label, logged, target }) {
  const hit = target > 0 && logged >= target;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`text-xs font-bold ${hit ? 'text-green-800 dark:text-green-400' : 'text-zinc-900 dark:text-white'}`}>
        {logged}g{target > 0 ? ` / ${target}g` : ''}
      </span>
      <span className="text-xs text-zinc-500 dark:text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
  );
}


// ─── Supplements Card ────────────────────────────────────────────────────────

function suppShortName(name) {
  let s = name.trim();
  // Replace "Vitamin" with short prefix, strip filler chemistry suffixes
  s = s.replace(/\b(vitamin|monohydrate|bisglycinate|glycinate|citrate|supplement|extract|formula|blend)\b/gi, '');
  // Clean up spaces around + and multiple spaces
  s = s.replace(/\s*\+\s*/g, '+').replace(/\s{2,}/g, ' ').trim();
  // Remove trailing/leading dots or punctuation artifacts
  s = s.replace(/^[.\s]+|[.\s]+$/g, '').trim();
  return s || name.trim();
}

function SupplementsCard({ supplements, urgent }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const { mutate: toggleSupp, isPending: suppPending } = useMutation({
    mutationFn: async ({ name, taken }) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const allItems = supplements.items || [];
      const entries = allItems.map(item => ({
        supplement_name: item.name,
        dosage: item.dosage,
        time_slot: item.times?.[0] || 'anytime',
        taken: item.name === name ? taken : item.taken,
      }));
      await client.post('/supplement-logs', { date: today, entries });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
    },
    onError: () => toast.error(t('dashboardExtras.failedUpdateSupp')),
  });

  const { mutate: markAllTaken, isPending: allPending } = useMutation({
    mutationFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const allItems = supplements.items || [];
      const entries = allItems.map(item => ({
        supplement_name: item.name,
        dosage: item.dosage,
        time_slot: item.times?.[0] || 'anytime',
        taken: true,
      }));
      await client.post('/supplement-logs', { date: today, entries });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
    },
    onError: () => toast.error(t('dashboardExtras.failedUpdateSupps')),
  });

  const items = supplements.items || [];

  // Has a plan but nothing scheduled today
  if (items.length === 0) {
    return (
      <div
        className="rounded-2xl py-4 px-4 backdrop-blur-md"
        style={{
          background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
          border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
          boxShadow: isDark ? undefined : '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Pill className="h-4 w-4 text-[#FF8C42]" />
          <span className="font-semibold text-sm text-zinc-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('dashboardExtras.supplementsTitle')}</span>
        </div>
        <p className="text-xs text-zinc-600 dark:text-gray-400">{t('dashboardExtras.noSuppsScheduled')}</p>
      </div>
    );
  }

  const GRID_DEFAULT = 8;
  const visibleItems = showAll ? items : items.slice(0, GRID_DEFAULT);
  const hasMore = items.length > GRID_DEFAULT;
  const done = supplements.all_taken;

  return (
    <TaskCard
      done={done}
      urgent={urgent}
      icon={<Pill className="h-5 w-5" />}
      title={t('dashboardExtras.supplementsTodayTitle')}
      color="blue"
      onCircleClick={done ? undefined : markAllTaken}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{
            background: done
              ? (isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.20)')
              : (isDark ? 'rgba(255,92,0,0.12)' : 'rgba(255,92,0,0.15)'),
            color: done
              ? (isDark ? '#4ade80' : '#15803d')
              : (isDark ? '#FF8C42' : '#c2410c'),
          }}
        >
          {done && supplements.total > 0
            ? `${supplements.total} / ${supplements.total} ✓`
            : t('dashboardExtras.suppsTakenCount', { taken: supplements.taken, total: supplements.total })}
        </span>
      </div>

      <div className={`grid grid-cols-3 sm:grid-cols-4 gap-2 ${suppPending ? 'opacity-60 pointer-events-none' : ''}`}>
        {visibleItems.map((item) => (
          <button
            key={item.name}
            onClick={() => { if (!suppPending) toggleSupp({ name: item.name, taken: !item.taken }); }}
            title={item.name}
            className="min-h-[52px] py-2 flex flex-col items-center justify-center rounded-xl transition-all active:scale-95 border px-1 gap-0.5"
            style={item.taken ? {
              background: isDark ? 'rgba(34,197,94,0.20)' : '#ffffff',
              borderColor: isDark ? 'rgba(34,197,94,0.50)' : 'rgba(34,197,94,0.55)',
            } : {
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            }}
          >
            <span
              className={`text-xs font-bold leading-tight text-center break-words w-full line-clamp-2 ${
                item.taken
                  ? 'text-green-800 dark:text-green-400'
                  : 'text-zinc-900 dark:text-white'
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {suppShortName(item.name)}
            </span>
            <span className={`text-[9px] leading-none text-center w-full truncate px-0.5 ${
              item.taken
                ? 'text-green-700 dark:text-gray-500'
                : 'text-zinc-500 dark:text-gray-500'
            }`}>
              {item.dosage}
            </span>
          </button>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="mt-3 text-xs text-primary flex items-center gap-1"
        >
          {showAll ? <><ChevronUp className="h-3 w-3" /> {t('dashboardExtras.showLess')}</> : <><ChevronDown className="h-3 w-3" /> {t('dashboardExtras.showAll', { count: items.length })}</>}
        </button>
      )}
    </TaskCard>
  );
}


// ─── Check-in Card ───────────────────────────────────────────────────────────

function CheckinCard({ checkin, urgent = false }) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dfLocale = i18n.language?.startsWith('pt') ? ptLocale : enLocale;
  const queryClient = useQueryClient();
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [skipping, setSkipping] = useState(false);

  const handleSkip = async () => {
    setSkipping(true);
    try {
      const res = await measurementsApi.skipCheckin(skipReason || null);
      const nextDate = res.data.next_check_in?.slice(0, 10);
      const formatted = nextDate ? format(new Date(nextDate), 'dd/MM/yyyy') : '';
      toast.success(t('checkIns.skipSuccess', { date: formatted }));
      setShowSkipDialog(false);
      setSkipReason('');
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (err) {
      toast.error(t('checkIns.skipFailed'));
    } finally {
      setSkipping(false);
    }
  };

  if (!checkin.enabled) {
    return (
      <TaskCard done={false} urgent={urgent} icon={<Camera className="h-5 w-5" />} title={t('checkinCard.title')} color="purple">
        <p className="text-sm text-zinc-600 dark:text-gray-400 mb-3">{t('checkinCard.noScheduleDesc')}</p>
        <Button
          asChild
          size="sm"
          className={`w-full border ${isDark ? 'text-white hover:bg-white/5' : 'text-zinc-900 hover:bg-black/5'}`}
          style={{
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
          }}
        >
          <Link to="/measurements">{t('checkinCard.startCheckin')}</Link>
        </Button>
      </TaskCard>
    );
  }

  if (checkin.completed_today) {
    const timeStr = formatTime(checkin.completed_at);
    return (
      <TaskCard done icon={<Camera className="h-5 w-5" />} title={t('checkinCard.title')} color="purple">
        <p className="text-sm text-green-800 dark:text-green-400 font-medium">
          {timeStr ? t('checkinCard.submittedAt', { time: timeStr }) : t('checkinCard.submittedToday')}
        </p>
      </TaskCard>
    );
  }

  const hoursLeft = checkin.hours_until_due ?? 999;
  const daysLeft = Math.floor(hoursLeft / 24);
  const remainingHours = Math.floor(hoursLeft % 24);

  const countdownText = hoursLeft <= -24
    ? t('checkinCard.overdue')
    : hoursLeft <= 0
    ? t('checkinCard.dueToday')
    : hoursLeft <= 24
    ? t('checkinCard.dueInHours', { hours: Math.floor(hoursLeft) })
    : t('checkinCard.nextIn', { days: daysLeft, hours: remainingHours });

  let scheduledText = '';
  if (checkin.next_due_date) {
    try {
      const dt = new Date(checkin.next_due_date);
      scheduledText = format(dt, 'EEEE, d MMM · HH:mm', { locale: dfLocale });
      scheduledText = scheduledText.charAt(0).toUpperCase() + scheduledText.slice(1);
    } catch (e) {
      scheduledText = '';
    }
  }

  const showSkip = hoursLeft <= 24;

  return (
    <>
      <TaskCard
        done={hoursLeft > 24}
        urgent={urgent}
        icon={<Camera className="h-5 w-5" />}
        title={t('checkinCard.title')}
        color="purple"
      >
        <p
          className={`text-lg font-bold ${urgent ? 'text-primary' : 'text-zinc-900 dark:text-white'}`}
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {countdownText}
        </p>
        {scheduledText && (
          <p className="text-xs text-zinc-600 dark:text-gray-400 mb-2">{scheduledText}</p>
        )}
        {hoursLeft <= 0 ? (
          <Button
            asChild
            className="w-full min-h-[52px] text-base font-bold text-white border-0 hover:opacity-95 animate-pulse"
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
              boxShadow: '0 4px 20px rgba(255,92,0,0.35)',
            }}
          >
            <Link to="/measurements">{t('checkinCard.checkInNow')}</Link>
          </Button>
        ) : (
          <Button asChild variant="ghost" size="sm" className="px-0 text-xs text-zinc-600 dark:text-gray-400">
            <Link to="/measurements">{t('checkinCard.checkInEarly')} <ChevronRight className="h-3 w-3 ml-1" /></Link>
          </Button>
        )}
        {showSkip && (
          <span
            role="button"
            tabIndex={0}
            className="inline-block mt-2 text-[11px] text-zinc-500 dark:text-gray-500 hover:text-zinc-700 dark:hover:text-gray-400 cursor-pointer"
            onClick={() => setShowSkipDialog(true)}
            onKeyDown={(e) => e.key === 'Enter' && setShowSkipDialog(true)}
          >
            {t('checkIns.skipCheckin')}
          </span>
        )}
      </TaskCard>

      <Dialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('checkIns.skipTitle').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t('checkIns.skipReason')}</label>
              <Textarea
                placeholder={t('checkIns.skipReasonPlaceholder')}
                value={skipReason}
                onChange={e => setSkipReason(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('checkIns.skipCoachNotice')}</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowSkipDialog(false)}>{t('checkIns.cancel')}</Button>
              <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={handleSkip} disabled={skipping}>
                {skipping ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{t('checkIns.skipping')}</> : t('checkIns.skipConfirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─── Stagger wrapper ─────────────────────────────────────────────────────────

function StaggerSlot({ index, animateIn, children }) {
  const delay = 300 + index * 80;
  return (
    <div
      className={`transition-all duration-700 ease-out ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}


// ─── TaskCard shell ───────────────────────────────────────────────────────────

function TaskCard({ done, rest, urgent, icon, title, color, onCircleClick, children }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const baseStyle = isDark
    ? {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.03) 100%)',
        backdropFilter: 'blur(20px) saturate(150%)',
        WebkitBackdropFilter: 'blur(20px) saturate(150%)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
        transition: 'all 300ms ease-out',
      }
    : {
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)',
        transition: 'all 300ms ease-out',
      };

  let stateStyle = {};
  if (done) {
    stateStyle = isDark
      ? {
          background: 'linear-gradient(135deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.03) 100%)',
          border: '1px solid rgba(34,197,94,0.35)',
          boxShadow: '0 4px 24px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
        }
      : {
          background: 'linear-gradient(135deg, rgba(34,197,94,0.22) 0%, rgba(34,197,94,0.08) 100%), #ffffff',
          border: '1px solid rgba(34,197,94,0.60)',
          boxShadow: '0 4px 28px rgba(34,197,94,0.25), 0 1px 2px rgba(0,0,0,0.03)',
        };
  } else if (rest) {
    stateStyle = isDark
      ? {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
        }
      : {
          background: 'linear-gradient(135deg, #fafafa 0%, #f4f4f5 100%)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        };
  } else if (urgent) {
    stateStyle = isDark
      ? {
          background: 'linear-gradient(135deg, rgba(40,20,10,0.85) 0%, rgba(25,15,10,0.9) 100%), linear-gradient(135deg, rgba(255,92,0,0.18) 0%, rgba(255,140,66,0.08) 100%)',
          backgroundBlendMode: 'normal',
          border: '1px solid rgba(255,92,0,0.55)',
          boxShadow: '0 0 32px rgba(255,92,0,0.35), 0 0 0 1px rgba(255,92,0,0.2) inset, 0 4px 24px rgba(0,0,0,0.4)',
        }
      : {
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          border: '1px solid rgba(255,92,0,0.45)',
          boxShadow: '0 0 28px rgba(255,92,0,0.18), 0 4px 16px rgba(255,92,0,0.10)',
        };
  }

  const mergedStyle = { ...baseStyle, ...stateStyle };

  let iconTileStyle;
  let iconColorClass;
  if (done) {
    iconTileStyle = { background: isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.20)' };
    iconColorClass = 'text-green-800 dark:text-green-400';
  } else if (rest) {
    iconTileStyle = { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' };
    iconColorClass = 'text-zinc-500 dark:text-gray-400';
  } else if (urgent) {
    iconTileStyle = {
      background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
      boxShadow: '0 0 20px rgba(255,92,0,0.45)',
    };
    iconColorClass = 'text-white';
  } else {
    const colorTint = {
      orange: isDark ? 'rgba(255,92,0,0.10)'  : 'rgba(255,92,0,0.12)',
      green:  isDark ? 'rgba(34,197,94,0.10)' : 'rgba(34,197,94,0.12)',
      blue:   isDark ? 'rgba(59,130,246,0.10)': 'rgba(59,130,246,0.12)',
      purple: isDark ? 'rgba(168,85,247,0.10)': 'rgba(168,85,247,0.12)',
    }[color] || (isDark ? 'rgba(255,92,0,0.10)' : 'rgba(255,92,0,0.12)');
    iconTileStyle = { background: colorTint };
    iconColorClass = {
      orange: 'text-[#FF8C42]',
      green:  'text-green-600 dark:text-green-400',
      blue:   'text-blue-600 dark:text-blue-400',
      purple: 'text-purple-600 dark:text-purple-400',
    }[color] || 'text-[#FF8C42]';
  }

  const circleEl = done ? (
    <div
      className="shrink-0 rounded-full flex items-center justify-center w-7 h-7"
      style={{ background: isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.22)' }}
    >
      <CheckCircle2 className="h-4 w-4 text-green-800 dark:text-green-400" />
    </div>
  ) : (
    <Circle className={`h-5 w-5 shrink-0 ${isDark ? 'text-white/20' : 'text-black/20'}`} />
  );

  return (
    <div className="relative rounded-2xl p-4" style={mergedStyle}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className={`relative w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconColorClass}`}
            style={iconTileStyle}
          >
            {icon}
            {urgent && !done && (
              <Sparkles
                className="absolute -top-1 -right-1 w-3 h-3 text-primary"
                style={{ animation: 'dashSparkle 2s ease-in-out infinite' }}
              />
            )}
          </div>
          <span
            className="text-xs font-bold tracking-[0.18em] text-zinc-500 dark:text-gray-400 uppercase truncate"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {title}
          </span>
        </div>
        {onCircleClick ? (
          <button
            onClick={onCircleClick}
            title={done ? 'All taken' : 'Mark all as taken'}
            className="shrink-0 rounded-full transition-transform active:scale-90 hover:opacity-80"
          >
            {circleEl}
          </button>
        ) : circleEl}
      </div>
      <div className={done ? (isDark ? 'opacity-60' : 'opacity-100') : ''}>
        {children}
      </div>
    </div>
  );
}
